import { useSetAtom } from 'jotai';
import { presentGuestSignupPromptAtom } from 'app/atoms';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'app/hooks/navigation';
import { Column } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';

import type { ResponsiveValue } from '@shopify/restyle/dist/types';
import { useLoginSheet } from 'app/screens/onboarding/login';
import { useUISize } from 'app/utils/web-helpers';
import { AccountsBlock } from '../index.large';
import { Platform, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Theme } from 'app/shopify';
const theme = createDSTheme();

interface DisconnectedPromptProps {
  backgroundColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  textColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  showIntro?: boolean;
}

export const DisconnectedPrompt = function DisconnectedPrompt({
  backgroundColor = 'surface.brand.default',
  textColor = 'text.inverse.primary',
  showIntro = true,
}: DisconnectedPromptProps) {
  const { t } = useTranslation('all-listings');
  const navigation = useNavigation();
  const { isLargeUI } = useUISize();

  const { showLoginSheet } = useLoginSheet();
  const setGuestPrompt = useSetAtom(presentGuestSignupPromptAtom);

  const onPressSignUp = () => {
    if (Platform.OS === 'web') {
      setGuestPrompt(true);
      return;
    }
    navigation.navigate(
      'onboarding',
      {
        screen: 'contact-information',
      },
      { pop: true },
    );
  };

  if (isLargeUI) {
    return (
      <Column alignItems="center">
        <View style={{ minWidth: '50%', marginBottom: -theme.spacing['spacing.general.x8'] }}>
          <AccountsBlock />
        </View>
      </Column>
    );
  }

  return (
    <Column
      backgroundColor={backgroundColor}
      borderRadius="radius.lg"
      gap="spacing.general.x6"
      marginTop="spacing.general.x2"
      padding="spacing.general.x6"
    >
      {showIntro ? (
        <Text
          color={textColor}
          fontWeight="700"
          paddingHorizontal="spacing.general.x6"
          textAlign="center"
          variant="bodyLarge"
        >
          {t('disconnected.text')}
        </Text>
      ) : null}

      <Column
        style={{
          boxShadow: '0px 11px 21px rgba(31, 31, 31, 0.35)',
          borderRadius: 30,
        }}
      >
        <Button onPress={onPressSignUp} variant="signup">
          {t('disconnected.buttonSignUp')}
        </Button>
      </Column>

      <Button
        onPress={showLoginSheet}
        style={{ height: 60, borderRadius: 30, borderColor: theme.colors['surface.default'], marginTop: -8 }}
        variant="secondary"
      >
        <Text color={textColor} fontWeight="700" variant="bodyBase">
          {t('disconnected.buttonLogIn')}
        </Text>
      </Button>
    </Column>
  );
};
